import { memo, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import Types
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import UI components
import { Button } from '@geneui/components';
// Import Components
import DataTable from 'components/common/DataTable';
import { AdminSMSProviderActionsButtons } from 'components/AdminSMSProvider/AdminSMSProvidersList';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
import useHasPermission from 'hooks/UseHasPermission';
// Import Services
import { AdministrationHttpService } from 'services/http';
// Import Configs
import { DataTableColumnsConfigs, getTableRows } from './config';
// Import Constants
import { l } from 'constants/common';
import { RoutesList } from 'routes';
// Import Enums
import { PermissionEnum } from 'enums';

const { ADMINISTRATION_CREATE_SMS_PROVIDER, ADMINISTRATION_EDIT_SMS_PROVIDER, ADMINISTRATION_CLONE_SMS_PROVIDER } =
    RoutesList;

interface Props {
    tableKey?: string;
    listFilters: UseListFiltersReturnValue;
}

interface DataTableRef {
    resetSelectedRows?: () => void;
    reset?: () => void;
}

const AdminSMSProvidersList = forwardRef<DataTableRef | null, Props>(
    ({ tableKey = 'adminProvidersDataTable', listFilters }, ref) => {
        const { t } = useTranslation();
        const { search } = useLocation();
        const { goToRoute } = useGoToRoute();
        const { doPostRequest } = useRequest();
        const { hasPermission } = useHasPermission();
        const goToRouteQueryParams = useQueryParams();
        const hasAdminPermission = !!hasPermission(PermissionEnum.CRMAdmin);

        const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

        const [isProviderListLoading, setIsProviderListLoading] = useState(false);

        const [providerListData, setProviderListData] = useState([]);
        const [providerListDataCount, setProviderListDataCount] = useState(0);

        const { columns } = DataTableColumnsConfigs(t);
        const dataTableRef = useRef<DataTableRef | null>();

        const { getSMSProvidersListRequest } = useCancelableRequests(
            { getSMSProvidersListRequest: AdministrationHttpService.getSMSProvidersList },
            { takeLatest: ['getSMSProvidersListRequest'] },
        );

        const getProviderList = useCallback(
            (filter = baseRequestBody) => {
                setIsProviderListLoading(true);
                doPostRequest(getSMSProvidersListRequest.request, {
                    requestBody: filter,
                    successCallback: ({ Data, Count }) => {
                        const rows = getTableRows(Data || []);
                        setProviderListData(rows);
                        setProviderListDataCount(Count);
                    },
                })?.then(({ IsCanceled }) => {
                    // A superseded call must not hide the loader of the call that replaced it
                    if (!IsCanceled) {
                        setIsProviderListLoading(false);
                    }
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [baseRequestBody],
        );

        const getHeaderActions = () => {
            return (
                <Button
                    flexibility="default"
                    icon="bc-icon-profit"
                    onClick={(e) => {
                        e.preventDefault();
                        goToRoute(
                            ADMINISTRATION_CREATE_SMS_PROVIDER,
                            undefined,
                            goToRouteQueryParams.encode({ backParams: search }),
                        );
                    }}
                    disabled={!hasAdminPermission}
                >
                    {t(l.CreateProvider)}
                </Button>
            );
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const goToActionPage = (route: any) => (provider: Record<string, any>) => {
            goToRoute(
                route,
                { SmsProviderId: provider.SmsProviderId },
                goToRouteQueryParams.encode({ backParams: search }),
            );
        };

        const getRowActionBar = (row) => {
            const { data } = row;

            return (
                <>
                    <AdminSMSProviderActionsButtons
                        data={data}
                        editProviderAction={goToActionPage(ADMINISTRATION_EDIT_SMS_PROVIDER)}
                        cloneProviderAction={goToActionPage(ADMINISTRATION_CLONE_SMS_PROVIDER)}
                    />
                </>
            );
        };

        const getTableRefreshFn = () => {
            getProviderList(baseRequestBody);
        };

        useImperativeHandle(ref, () => ({
            resetSelectedRows() {
                dataTableRef.current?.resetSelectedRows?.();
            },
            reset() {
                dataTableRef.current?.reset?.();
            },
        }));

        useEffect(() => {
            if (isMounted.current) {
                getProviderList(baseRequestBody);
            }
            /* eslint-disable react-hooks/exhaustive-deps */
        }, [baseRequestBody]);

        return (
            <DataTable
                classNames="with-filter-tags-and-page-headline-height"
                name={l.SMSProviders}
                rowKey="rowKey"
                columnKey="dataKey"
                tableKey={tableKey}
                isColumnsSortable={true}
                data={providerListData}
                dataCount={providerListDataCount}
                rowActionBar={getRowActionBar}
                columns={columns}
                isLoading={isProviderListLoading}
                isShowRefreshButton={true}
                isShowColumnChooser={true}
                setPagingPageSize={changePageSize}
                setPagingPageNumber={changePageNumber}
                sorting={baseRequestBody.Sorting}
                setSorting={changeSorting}
                onRefreshClick={getTableRefreshFn}
                headerActions={getHeaderActions()}
                isHasArchiveActions={false}
                isHasUnarchiveActions={false}
                receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                withQueryParams={true}
                ref={dataTableRef}
            />
        );
    },
);

AdminSMSProvidersList.displayName = 'AdminProviderList';

export default memo(AdminSMSProvidersList);
